import React, { useState, useRef } from "react";
import { Button, Modal } from "react-bootstrap";
import { Upload, FileText, X } from "lucide-react";
import { uploadFile } from "../../../../../../services/fileServices/UserDriverService";
import Loader from "../../../../loader/Loader";
import { toast } from "react-toastify";
import {
  ALLOWED_FILE_MIMETYPES,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
  MAX_FILES_UPLOAD,
} from "../../../../../../Data/SD";
import "./FileUploadModal.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";

interface FileUploadModalProps {
  showFileModal: boolean;
  handleCloseFileModal: () => void;
  authToken: string;
  refreshCurrentFolder: () => void;
  currentFolderId?: number;
}

const FileUploadModal = ({
  handleCloseFileModal,
  showFileModal,
  authToken,
  refreshCurrentFolder,
  currentFolderId,
}: FileUploadModalProps) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [uploadResults, setUploadResults] = useState<{
    success: string[];
    failed: string[];
  }>({
    success: [],
    failed: [],
  });
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  // Validate and add files
  const validateAndAddFiles = (files: File[]) => {
    let filesToAdd = Array.from(files);

    // Check max files limit
    const remainingSlots = MAX_FILES_UPLOAD - selectedFiles.length;
    if (filesToAdd.length > remainingSlots) {
      toast.warning(
        `You can only upload ${MAX_FILES_UPLOAD} files total. Adding first ${remainingSlots} file(s).`
      );
      filesToAdd = filesToAdd.slice(0, remainingSlots);
    }

    const validFiles: File[] = [];

    filesToAdd.forEach((file) => {
      // Check file size
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.warning(
          `"${file.name}" exceeds ${MAX_FILE_SIZE_MB} MB. Skipped.`
        );
        return;
      }

      // Check if file already exists
      const isDuplicate = selectedFiles.some(
        (f) => f.name === file.name && f.size === file.size
      );
      if (isDuplicate) {
        toast.info(`"${file.name}" is already selected.`);
        return;
      }

      validFiles.push(file);
    });

    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles]);
    }
  };

  // Handle file input change
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      validateAndAddFiles(files);
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle drag events
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      validateAndAddFiles(files);
    }
  };

  // Remove individual file
  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Browse files button click
  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  // Upload all selected files
  const handleUpload = async () => {
    if (!selectedFiles.length) return;

    setLoading(true);
    setUploadResults({ success: [], failed: [] });

    const uploadPromises = selectedFiles.map((file) => {
      const formData = new FormData();
      formData.append("File", file);
      if (currentFolderId) {
        formData.append("FolderID", currentFolderId.toString());
      }
      formData.append("FileName", file.name);
      formData.append("Size", file.size.toString());
      formData.append("MimeType", file.type || "application/octet-stream");

      return uploadFile(authToken, formData)
        .then(() => ({ status: "fulfilled", file: file.name }))
        .catch(() => ({ status: "rejected", file: file.name }));
    });

    try {
      const results = await Promise.allSettled(uploadPromises);

      const success: string[] = [];
      const failed: string[] = [];

      results.forEach((r) => {
        if (r.status === "fulfilled" && r.value.status === "fulfilled") {
          success.push(r.value.file);
        } else if (r.status === "fulfilled" && r.value.status === "rejected") {
          failed.push(r.value.file);
        } else if (r.status === "rejected") {
          console.error("Unexpected error:", r.reason);
        }
      });

      setUploadResults({ success, failed });
      setSelectedFiles([]);

      if (success.length) {
        toast.success(`${success.length} file(s) uploaded successfully`);
        handleModalClose();
      }
      if (failed.length) {
        toast.error(`${failed.length} file(s) failed to upload`);
      }

      refreshCurrentFolder();
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setSelectedFiles([]);
    setUploadResults({ success: [], failed: [] });
    setIsDragging(false);
    handleCloseFileModal();
  };

  return (
    <>
      {loading && <Loader />}
      <Modal
        show={showFileModal}
        onHide={handleModalClose}
        className="custom_popup"
        centered
        backdrop="static"
        keyboard={false}
      >
        <div className="modal-activity-reject-header">
          <h2 className="modal-activity-reject-title">Upload Files</h2>
          <button
            className="modal-activity-reject-close"
            onClick={(e) => {
              handleModalClose();
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <Modal.Body>
          <div className="upload-area-wrapper !mb-[1.25rem]">
            <div
              className={`upload-area p-[2.12rem] ${
                isDragging ? "drag-over" : ""
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={handleBrowseClick}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileChange}
                accept={ALLOWED_FILE_MIMETYPES.join(",")}
                style={{ display: "none" }}
              />

              <div className="upload-icon w-[4rem] h-[4rem] mb-[1rem]">
                <Upload className="w-[2rem] h-[2rem]" />
              </div>

              <div className="upload-text mb-[0.25rem]">
                <p>
                  <span className="click-text !font-Arial !text-[#7E57C2]">
                    Click to upload
                  </span>{" "}
                  or drag and drop
                </p>
              </div>

              <div className="upload-size-info">
                Max Size: {MAX_FILE_SIZE_MB}MB
              </div>

              <button
                type="button"
                className="browse-btn !py-[0.75rem] text-[#0A0A0A] !bg-[#F9F6FF] !text-[0.875rem] !px-[1.62rem]"
                onClick={(e) => {
                  e.stopPropagation();
                  handleBrowseClick();
                }}
              >
                Browse Files
              </button>
            </div>
          </div>

          {/* Selected Files */}
          {selectedFiles.length > 0 && (
            <div className="selected-files-section">
              <div className="selected-files-title">
                Selected Files ({selectedFiles.length})
              </div>
              {selectedFiles.map((file, index) => (
                <div key={index} className="file-item !mb-[1.25rem]">
                  {/* <div className="file-icon">
                    <FileText />
                  </div> */}
                  <div className="file-icon !bg-[#FFE2E2] !rounded-[0.520rem]">
                    {/* <FileText /> */}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="1.25rem"
                      height="1.25rem"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M3.5 4C3.5 3.46957 3.71071 2.96086 4.08579 2.58579C4.46086 2.21071 4.96957 2 5.5 2H10.086C10.6164 2.00011 11.125 2.2109 11.5 2.586L14.914 6C15.2891 6.37499 15.4999 6.88361 15.5 7.414V16C15.5 16.5304 15.2893 17.0391 14.9142 17.4142C14.5391 17.7893 14.0304 18 13.5 18H5.5C4.96957 18 4.46086 17.7893 4.08579 17.4142C3.71071 17.0391 3.5 16.5304 3.5 16V4Z"
                        fill="#E7000B"
                      />
                    </svg>
                  </div>
                  <div className="file-info">
                    <p className="file-name !text-[#314158] !px-0">
                      {file.name}
                    </p>
                    <p className="file-size">{formatFileSize(file.size)}</p>
                  </div>
                  <button
                    type="button"
                    className="file-remove"
                    onClick={() => handleRemoveFile(index)}
                    aria-label="Remove file"
                  >
                    <X />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Upload Results */}
          {(uploadResults.success.length > 0 ||
            uploadResults.failed.length > 0) && (
            <div className="upload-results">
              {uploadResults.success.length > 0 && (
                <>
                  <h6 className="text-success">Uploaded:</h6>
                  <ul>
                    {uploadResults.success.map((name, i) => (
                      <li key={i}>{name}</li>
                    ))}
                  </ul>
                </>
              )}
              {uploadResults.failed.length > 0 && (
                <>
                  <h6 className="text-danger">Failed:</h6>
                  <ul>
                    {uploadResults.failed.map((name, i) => (
                      <li key={i}>{name}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="!pt-[1rem] border-t !mb-[2.6rem] !mx-[1.56rem] border-black/10">
          <Button
            className="btn btn-secondary cancel-btn"
            onClick={handleModalClose}
          >
            Cancel
          </Button>
          <Button
            className="btn btn-primary submit-btn"
            onClick={handleUpload}
            disabled={!selectedFiles.length || loading}
          >
            {loading
              ? "Uploading..."
              : `Upload${
                  selectedFiles.length > 0 ? ` (${selectedFiles.length})` : ""
                }`}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default FileUploadModal;
